import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import { videos } from "@/mocks/videos";
import { getFeaturedVideos, searchVideos } from "@/utils/video";
import { formatRating } from "@/utils/format";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  useDocumentMeta({
    title: q ? `“${q}”搜索结果 - 在线播放` : "搜索视频 - 在线播放",
    description: "在在线播放搜索电影、电视剧、综艺节目，快速找到你想看的内容。",
  });

  const results = useMemo(() => searchVideos(q), [q]);
  const hot = getFeaturedVideos();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    setParams(kw ? { q: kw } : {});
  };

  return (
    <div className="pt-16 md:pt-20 px-4 md:px-8 py-8 md:py-12">
      {/* Search bar */}
      <div className="max-w-3xl">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {q ? "搜索结果" : "搜索视频"}
        </h1>
        <form onSubmit={submit} className="flex items-center gap-2 md:gap-3 mt-5">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入影片名称、类型或地区"
              className="w-full pl-11 pr-4 py-3 text-sm rounded-md bg-background-100 border border-background-300/70 text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50"
            />
          </div>
          <button
            type="submit"
            className="px-5 md:px-7 py-3 rounded-md font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      {q ? (
        <p className="mt-6 text-sm text-foreground-600">
          {results.length > 0 ? (
            <>
              关键词 <span className="text-accent-500 font-medium">“{q}”</span> 共找到{" "}
              <span className="text-accent-500 font-semibold">{results.length}</span> 个结果
            </>
          ) : (
            <>未找到与 <span className="text-accent-500 font-medium">“{q}”</span> 相关的内容</>
          )}
        </p>
      ) : (
        <p className="mt-6 text-sm text-foreground-600">输入关键词，开始发现想看的内容。</p>
      )}

      {/* Results */}
      {q && results.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5 mt-6">
          {results.map((video, i) => (
            <VideoCard key={video.id} video={video} index={i} />
          ))}
        </div>
      )}

      {/* Empty state + hot recommendations */}
      {q && results.length === 0 && (
        <div className="mt-6 flex flex-col items-center justify-center py-16 rounded-lg bg-background-100 border border-background-300/50">
          <span className="w-16 h-16 rounded-full bg-background-200/70 flex items-center justify-center">
            <i className="ri-search-eye-line text-3xl text-foreground-500" />
          </span>
          <p className="mt-4 text-foreground-800 font-medium">未找到相关视频</p>
          <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </div>
      )}

      {/* Hot section when no keyword or no results */}
      {(!q || results.length === 0) && (
        <section className="mt-12">
          <div className="flex items-center gap-2 mb-6">
            <i className="ri-fire-fill text-accent-500 text-xl" />
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">热门推荐</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
            {hot.map((video, i) => (
              <VideoCard key={video.id} video={video} index={i} />
            ))}
          </div>

          <div className="mt-10">
            <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-4">大家都在看</h3>
            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...videos]
                .sort((a, b) => b.views - a.views)
                .slice(0, 6)
                .map((v, i) => (
                  <li key={v.id}>
                    <Link
                      to={`/detail/${v.id}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-300/40 hover:border-primary-400/60 transition-colors cursor-pointer"
                    >
                      <span className="w-6 text-center font-heading font-extrabold text-accent-500">{i + 1}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground-950 truncate">{v.title}</span>
                        <span className="block mt-0.5 text-xs text-foreground-500">
                          {v.typeLabel} · {v.year} · 评分 {formatRating(v.rating)}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-500" />
                    </Link>
                  </li>
                ))}
            </ol>
          </div>
        </section>
      )}
    </div>
  );
}